export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogOptions {
  class?: string
  level?: LogLevel
}

export interface AppLogger {
  debug(message: string, options?: LogOptions): void
  info(message: string, options?: LogOptions): void
  warn(message: string, options?: LogOptions): void
  error(message: string, options?: LogOptions): void
}

type LoggerGlobal = typeof globalThis & { logger?: AppLogger }

const logLevels = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

function write(level: LogLevel, message: string, options: LogOptions): void {
  const logger = (globalThis as LoggerGlobal).logger
  if (logger) {
    logger[level](message, options)
    return
  }
  console[level](message, options)
}

const GlobalLogger = {
  debug: (message: string, options: LogOptions = {}) => write('debug', message, options),
  info: (message: string, options: LogOptions = {}) => write('info', message, options),
  warn: (message: string, options: LogOptions = {}) => write('warn', message, options),
  error: (message: string, options: LogOptions = {}) => write('error', message, options),
  bind: (className: string, options: LogOptions = { level: 'info' }) => {
    const log = (level: LogLevel, message: string) => write(level, message, { class: className })
    const level = options.level ?? 'info'
    const levelIndex = logLevels[level]

    return {
      debug: (message: string) => {
        if (levelIndex > logLevels.debug) return
        log('debug', message)
      },
      info: (message: string) => {
        if (levelIndex > logLevels.info) return
        log('info', message)
      },
      warn: (message: string) => {
        if (levelIndex > logLevels.warn) return
        log('warn', message)
      },
      error: (message: string) => {
        if (levelIndex > logLevels.error) return
        log('error', message)
      },
      catch: <T>(func: () => T) => {
        try {
          return func()
        } catch (err) {
          log('error', err instanceof Error ? (err.stack ?? err.message) : String(err))
          return undefined
        }
      },
    }
  },
}
export default GlobalLogger
